// CSS typography resolver — the length/font-family sibling of cssColorResolver.
//
// The canvas plane rasterizers (text/note/node-chrome) paint with a 2D context, which
// can NOT read CSS variables. To keep the BAKED text in EXACT parity with the DOM node
// (the user's "labels switch font size + color when I select" report), the rasterizer
// must resolve the SAME `--text-*` / `--font-ui` design tokens the DOM control CSS uses
// — NOT a hard-coded px guess. This module is the single resolver for that, mirroring
// `resolveCssColorValue`: probe-element measurement in the browser, deterministic px
// fallback when there is no DOM (headless test / SSR), and a small theme-keyed cache.

const cssLengthCache = new Map();
let cachedFontFamily = null;

/**
 * Resolve a CSS length expression (e.g. `var(--text-body)`, `0.875rem`, `14px`) to a
 * pixel number. `fallbackPx` is returned verbatim when there is no DOM or the value can
 * not be measured (so a bake is ALWAYS produced). In the browser the live computed value
 * wins → exact parity with the DOM control that uses the same token.
 */
export function resolveCssLengthPx(length, fallbackPx, options = {}) {
  const allowZero = options?.allowZero === true;
  const isAllowedLength = (value) => (
    Number.isFinite(value) && (allowZero ? value >= 0 : value > 0)
  );
  const safeFallback = isAllowedLength(fallbackPx) ? fallbackPx : (allowZero ? 0 : 12);
  if (typeof length !== 'string' || !length.trim()) return safeFallback;

  // A bare px / unitless number short-circuits (no DOM needed).
  const bare = /^(-?\d*\.?\d+)px$/.exec(length.trim());
  if (bare) {
    const n = Number.parseFloat(bare[1]);
    return isAllowedLength(n) ? n : safeFallback;
  }

  if (typeof document === 'undefined' || typeof window === 'undefined') {
    return safeFallback;
  }

  const {
    contextElement = document.documentElement,
    cacheKey: contextCacheKey = 'root',
  } = options || {};
  const cascadeElement = contextElement || document.documentElement;
  const cssVars = [...length.matchAll(/var\(\s*(--[\w-]+)/g)].map((match) => match[1]);
  if (cssVars.length > 0) {
    const cascadeStyle = cascadeElement
      ? window.getComputedStyle(cascadeElement)
      : null;
    const hasMissingVar = cssVars.some((name) => !cascadeStyle?.getPropertyValue(name).trim());
    if (hasMissingVar) {
      return safeFallback;
    }
  }

  const theme = document.documentElement?.getAttribute('data-theme') || '';
  const cacheKey = contextCacheKey ? `${theme}:${contextCacheKey}:${length}` : null;
  if (cacheKey) {
    const cached = cssLengthCache.get(cacheKey);
    if (cached != null) return cached;
  }

  const mount = document.body || document.documentElement;
  if (!mount) return safeFallback;

  // font-size resolves `rem`/`em`/`var()` against the cascade exactly as the DOM control
  // would, and getComputedStyle reports it back in px.
  const probe = document.createElement('span');
  probe.style.position = 'absolute';
  probe.style.visibility = 'hidden';
  probe.style.pointerEvents = 'none';
  probe.style.fontSize = length;
  const probeParent = cascadeElement || mount;
  probeParent.appendChild(probe);
  const resolved = window.getComputedStyle(probe).fontSize;
  probe.remove();

  const px = Number.parseFloat(resolved);
  const next = isAllowedLength(px) ? px : safeFallback;
  if (cacheKey) {
    cssLengthCache.set(cacheKey, next);
  }
  return next;
}

/**
 * Resolve `--font-ui` (the DOM node card family) to a usable canvas font stack. Mirrors
 * `resolveNoteCanvasFontFamily` so node chrome and notes share the SAME resolved family.
 * Returns `fallbackStack` (a concrete stack) when there is no DOM.
 */
export function resolveCssFontFamily(fallbackStack = "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif") {
  if (cachedFontFamily) return cachedFontFamily;
  if (typeof window === 'undefined' || typeof document === 'undefined' || !document.documentElement) {
    return fallbackStack;
  }
  const resolved = window
    .getComputedStyle(document.documentElement)
    ?.getPropertyValue('--font-ui')
    ?.trim();
  cachedFontFamily = resolved || fallbackStack;
  return cachedFontFamily;
}

// A `--font-mono` resolver used to live here. Its only consumer was the node-chrome
// rasterizer's slider readout, which existed to mirror `.param-value-label` while that class
// was `font-mono`. The DOM readout is now --font-ui, so the readout bakes with the same
// family as every other string on the canvas and the second resolver has no caller.

// Test seam: reset the memo caches between cases.
export function __resetCssTypographyResolverCachesForTest() {
  cssLengthCache.clear();
  cachedFontFamily = null;
}
